import {
  PROJECT_CATALOG_PRODUCTS,
  PROJECT_PACKAGE_DETAIL,
  PROJECT_PACKAGE_LIST,
  PROJECT_QUESTION_LIST,
  PROJECT_TYPE_DETAIL,
  PROJECT_TYPE_LIST,
} from "graphql/projectType/query";
import {
  CREATE_PROJECT_PACKAGE,
  CREATE_PROJECT_QUESTION,
  CREATE_PROJECT_TYPE,
  DELETE_PROJECT_PACKAGE,
  DELETE_PROJECT_QUESTION,
  REORDER_PROJECT_QUESTIONS,
  SET_PROJECT_PACKAGE_VISIBILITY,
  SET_PROJECT_TYPE_VISIBILITY,
  UPDATE_PROJECT_PACKAGE,
  UPDATE_PROJECT_QUESTION,
  UPDATE_PROJECT_TYPE,
} from "graphql/projectType/mutation";
import {
  convertCatalogProductPage,
  convertProjectPackageDetail,
  convertProjectPackageItem,
  convertProjectPackages,
  convertProjectQuestion,
  convertProjectQuestions,
  convertProjectType,
  convertProjectTypePage,
} from "./converter";

// TC-41: mỗi lần mở màn đều lấy dữ liệu mới; errorPolicy=all để lỗi GraphQL không bị bỏ qua.
const QUERY_OPTIONS = { fetchPolicy: "network-only", errorPolicy: "all" };

/** Lỗi GraphQL trả kèm data (errorPolicy=all) được ném như lỗi reject để caller xử lý một kiểu. */
export class ProjectTypeGraphQLError extends Error {
  constructor(graphQLErrors) {
    super(graphQLErrors[0]?.message || "GraphQL error");
    this.graphQLErrors = graphQLErrors;
  }
}

const pick = (field, convert) => (result) => {
  if (result?.errors?.length) throw new ProjectTypeGraphQLError(result.errors);
  return convert(result?.data?.projectConfig?.[field]);
};

const query = (client, document, variables, field, convert) =>
  client.query({ query: document, variables, ...QUERY_OPTIONS }).then(pick(field, convert));

const mutate = (client, document, variables, field, convert) =>
  client.mutate({ mutation: document, variables, errorPolicy: "all" }).then(pick(field, convert));

const asNull = () => null;

export const gqlListProjectTypes = (client, { keyword, page, size }) =>
  query(client, PROJECT_TYPE_LIST, { keyword, page, size }, "listProjectTypes", (value) =>
    convertProjectTypePage(value, { page, size })
  );
export const gqlGetProjectType = (client, id) =>
  query(client, PROJECT_TYPE_DETAIL, { id }, "getProjectType", convertProjectType);
export const gqlCreateProjectType = (client, input) =>
  mutate(client, CREATE_PROJECT_TYPE, { input }, "createProjectType", convertProjectType);
export const gqlUpdateProjectType = (client, id, input) =>
  mutate(client, UPDATE_PROJECT_TYPE, { id, input }, "updateProjectType", convertProjectType);
export const gqlSetProjectTypeVisibility = (client, id, isVisible) =>
  mutate(client, SET_PROJECT_TYPE_VISIBILITY, { id, isVisible }, "setProjectTypeVisibility", convertProjectType);
export const gqlListProjectQuestions = (client, projectTypeId) =>
  query(client, PROJECT_QUESTION_LIST, { projectTypeId }, "listProjectQuestions", convertProjectQuestions);
export const gqlCreateProjectQuestion = (client, projectTypeId, input) =>
  mutate(client, CREATE_PROJECT_QUESTION, { projectTypeId, input }, "createProjectQuestion", convertProjectQuestion);
export const gqlUpdateProjectQuestion = (client, id, input) =>
  mutate(client, UPDATE_PROJECT_QUESTION, { id, input }, "updateProjectQuestion", convertProjectQuestion);
export const gqlDeleteProjectQuestion = (client, id) =>
  mutate(client, DELETE_PROJECT_QUESTION, { id }, "deleteProjectQuestion", asNull);
export const gqlReorderProjectQuestions = (client, projectTypeId, questionIds) =>
  mutate(
    client,
    REORDER_PROJECT_QUESTIONS,
    { projectTypeId, questionIds },
    "reorderProjectQuestions",
    convertProjectQuestions
  );
export const gqlListProjectPackages = (client, projectTypeId) =>
  query(client, PROJECT_PACKAGE_LIST, { projectTypeId }, "listProjectPackages", convertProjectPackages);
export const gqlGetProjectPackage = (client, id) =>
  query(client, PROJECT_PACKAGE_DETAIL, { id }, "getProjectPackage", convertProjectPackageDetail);
export const gqlCreateProjectPackage = (client, input) =>
  mutate(client, CREATE_PROJECT_PACKAGE, { input }, "createProjectPackage", convertProjectPackageDetail);
export const gqlUpdateProjectPackage = (client, id, input) =>
  mutate(client, UPDATE_PROJECT_PACKAGE, { id, input }, "updateProjectPackage", convertProjectPackageDetail);
export const gqlDeleteProjectPackage = (client, id) =>
  mutate(client, DELETE_PROJECT_PACKAGE, { id }, "deleteProjectPackage", asNull);
export const gqlSetProjectPackageVisibility = (client, id, isVisible) =>
  mutate(
    client,
    SET_PROJECT_PACKAGE_VISIBILITY,
    { id, isVisible },
    "setProjectPackageVisibility",
    convertProjectPackageItem
  );
export const gqlSearchCatalogProducts = (client, { keyword, page, size }) =>
  query(client, PROJECT_CATALOG_PRODUCTS, { keyword, page, size }, "searchCatalogProducts", (value) =>
    convertCatalogProductPage(value, { page, size })
  );
